import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  ROTULO_DA_VISTA,
  ROTULO_DO_MODELO,
  ROTULO_DO_TIPO_DE_MARCACAO,
  TIPOS_DE_MARCACAO,
  VISTAS_DA_FACE,
  espelharMarcacao,
  type MarcacaoFacial,
  type ModeloDaFace,
  type TamanhoDaMarcacao,
  type VistaDaFace,
} from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { MODELOS_DISPONIVEIS } from "@/components/face/desenhos";
import { EditorDaFace, type Ferramenta, type MarcacaoDeOutroItem } from "@/components/face/EditorDaFace";
import { comTamanho } from "@/components/face/geometria";
import { Segmentado } from "@/components/face/Segmentado";
import { PainelDaMarcacao } from "./PainelDaMarcacao";

const seta =
  "absolute top-1/2 z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-sm hover:text-zinc-900 disabled:opacity-30 dark:border-border dark:bg-card";

/**
 * O modo Face do plano: a vista do rosto com as marcações dos itens e as do item em montagem.
 * Setas trocam a vista (perfil direito, frente, perfil esquerdo); a ferramenta escolhe o tipo da
 * próxima marcação e o tamanho rápido. A vista acompanha o item destacado na lista.
 */
export function FaceDoPlano({
  modelo,
  onModelo,
  editavel,
  outras,
  proprias,
  onMudar,
  cor,
  unidade,
  chave,
  vistaDoDestaque,
  destacadoNome,
  onClicarItem,
  onLimparDestaque,
}: {
  modelo: ModeloDaFace;
  onModelo: (m: ModeloDaFace) => void;
  editavel: boolean;
  outras: MarcacaoDeOutroItem[];
  proprias: MarcacaoFacial[];
  onMudar: (lista: MarcacaoFacial[]) => void;
  cor: string;
  unidade: string | null;
  /** Muda quando a montagem recomeça (item novo ou outro item em edição). */
  chave: number;
  /** Vista da primeira marcação do item destacado na lista — a face vira pra ela. */
  vistaDoDestaque: VistaDaFace | null;
  destacadoNome: string | null;
  onClicarItem: (itemId: string) => void;
  onLimparDestaque: () => void;
}) {
  const [vista, setVista] = useState<VistaDaFace>(proprias[0]?.vista ?? "FRENTE");
  const [ferramenta, setFerramenta] = useState<Ferramenta>({ tipo: "AREA", tamanho: "M" });
  const [selecionada, setSelecionada] = useState<number | null>(null);

  useEffect(() => {
    setSelecionada(null);
    if (proprias[0]) setVista(proprias[0].vista);
    // Só quando a montagem recomeça; `proprias` muda a cada arraste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);
  useEffect(() => {
    if (vistaDoDestaque) setVista(vistaDoDestaque);
  }, [vistaDoDestaque]);

  const i = VISTAS_DA_FACE.indexOf(vista);
  const comMarcacao = (v: VistaDaFace) => proprias.some((m) => m.vista === v) || outras.some((o) => o.m.vista === v);
  const sel = selecionada != null ? proprias[selecionada] : null;
  const mudarTamanho = (tamanho: TamanhoDaMarcacao) => {
    setFerramenta((f) => ({ ...f, tamanho }));
    if (sel && selecionada != null) onMudar(proprias.map((m, k) => (k === selecionada ? comTamanho(m, tamanho) : m)));
  };

  return (
    <div className="space-y-3">
      {MODELOS_DISPONIVEIS.length > 1 && (
        <Segmentado rotulo="Modelo do desenho" valor={modelo} onMudar={onModelo} opcoes={MODELOS_DISPONIVEIS.map((v) => ({ valor: v, rotulo: ROTULO_DO_MODELO[v] }))} />
      )}
      {editavel && (
        <div className="space-y-1.5">
          <Segmentado
            rotulo="Tipo de marcação"
            valor={ferramenta.tipo}
            onMudar={(tipo) => setFerramenta((f) => ({ ...f, tipo }))}
            opcoes={TIPOS_DE_MARCACAO.map((t) => ({ valor: t, rotulo: ROTULO_DO_TIPO_DE_MARCACAO[t] }))}
          />
          {(ferramenta.tipo === "AREA" || ferramenta.tipo === "ELIPSE") && (
            <Segmentado
              rotulo="Tamanho do desenho"
              valor={ferramenta.tamanho}
              onMudar={mudarTamanho}
              opcoes={(["P", "M", "G"] as const).map((t) => ({ valor: t, rotulo: t, titulo: "Só o tamanho do desenho, não a dose" }))}
            />
          )}
        </div>
      )}
      <div className="relative px-6">
        <button type="button" className={cn(seta, "left-0")} disabled={i === 0} onClick={() => setVista(VISTAS_DA_FACE[i - 1])} aria-label={i > 0 ? ROTULO_DA_VISTA[VISTAS_DA_FACE[i - 1]] : "Sem vista anterior"}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <EditorDaFace
          // Trocar de vista descarta o trajeto pela metade.
          key={vista}
          modelo={modelo}
          vista={vista}
          editavel={editavel}
          outras={outras}
          proprias={proprias}
          cor={cor}
          ferramenta={ferramenta}
          selecionada={selecionada}
          onSelecionar={setSelecionada}
          onMudar={onMudar}
          onClicarItem={onClicarItem}
        />
        <button
          type="button"
          className={cn(seta, "right-0")}
          disabled={i === VISTAS_DA_FACE.length - 1}
          onClick={() => setVista(VISTAS_DA_FACE[i + 1])}
          aria-label={i < VISTAS_DA_FACE.length - 1 ? ROTULO_DA_VISTA[VISTAS_DA_FACE[i + 1]] : "Sem próxima vista"}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center justify-center gap-3">
        {VISTAS_DA_FACE.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVista(v)}
            className={cn("flex items-center gap-1 text-[11px]", v === vista ? "font-semibold text-brand-700 dark:text-primary" : "text-zinc-500 hover:text-zinc-800")}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", comMarcacao(v) ? "bg-current" : "border border-current")} />
            {ROTULO_DA_VISTA[v]}
          </button>
        ))}
      </div>
      {sel && editavel && (
        <PainelDaMarcacao
          key={selecionada}
          m={sel}
          numero={selecionada! + 1}
          unidade={unidade}
          onMudar={(m) => onMudar(proprias.map((x, k) => (k === selecionada ? m : x)))}
          onEspelhar={() => {
            onMudar([...proprias, espelharMarcacao(sel)]);
            setSelecionada(proprias.length);
            setVista(espelharMarcacao(sel).vista);
          }}
          onRemover={() => {
            onMudar(proprias.filter((_, k) => k !== selecionada));
            setSelecionada(null);
          }}
        />
      )}
      <p className="text-center text-[11.5px] text-zinc-500 dark:text-muted-foreground">
        {destacadoNome ? (
          <>
            Destacando <b className="text-zinc-700 dark:text-foreground">{destacadoNome}</b> ·{" "}
            <button type="button" className="font-semibold text-brand-700 hover:underline" onClick={onLimparDestaque}>
              mostrar todos
            </button>
          </>
        ) : editavel ? (
          ferramenta.tipo === "TRAJETO" ? "Clique os pontos do trajeto no rosto." : "Clique no rosto para marcar; arraste para mover."
        ) : (
          "Clique numa marcação para ver o procedimento."
        )}
      </p>
    </div>
  );
}
